/**
 * AchievementsBlock — the badge rail on a surface.
 *
 * Falls back to the public achievements endpoint for unauthenticated visitors:
 * definitions only, no progress.
 */
import React, {useState, useMemo, useEffect} from "react";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {useGetDropAchievements, MyDropAchievementEntry} from "@/hooks/drop/useGetDropAchievements";
import {BadgeUnlockModal, UnlockedBadge} from "@/widgets/BadgeUnlockModal";
import "./AchievementsBlock.scss";

interface AchievementsBlockProps {
    surfaceSlug?: string;
    /** The block's authored section title ("Badges"). */
    sectionTitle?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
    COMPETITION: "Competition",
    COMMUNITY: "Community",
    ENGAGEMENT: "Engagement",
    MILESTONE: "Milestone",
};

const CONDITION_LABELS: Record<string, string> = {
    POINTS_EARNED: "Points",
    CHALLENGE_WINS: "Wins",
    CHALLENGE_PARTICIPATIONS: "Participations",
    LEADERBOARD_TOP_N: "Top Finishes",
    REFERRAL_COUNT: "Referrals",
    STREAK_DAYS: "Streak Milestones",
    MEMBER_DAYS: "Days",
    SPONSOR_ENGAGEMENTS: "Engagements",
};

function AchievementBadge({item}: { item: MyDropAchievementEntry }) {
    const {achievement, unlocked, progress} = item;

    // Compute overall progress percentage (average of all conditions)
    const overallProgress = useMemo(() => {
        if (unlocked || progress.length === 0) return 100;
        /*
         * Average across all conditions — so partial progress on any condition
         * contributes to the bar, rather than gating on the hardest condition.
         */
        const total = progress.reduce((sum, p) => sum + Math.min(p.threshold > 0 ? p.current / p.threshold : 1, 1), 0);
        return Math.round(total / progress.length * 100);
    }, [unlocked, progress]);

    return (
        <div className={`achievements-badge ${unlocked ? "achievements-badge--unlocked" : "achievements-badge--locked"}`}>
            <div className="achievements-badge_icon">
                {achievement.iconUrl
                    ? <img
                        src={achievement.iconUrl}
                        alt={achievement.name}
                        className="achievements-badge_img"
                    />
                    /*
                     * A rosette, never the name's first letter. A monogram
                     * reads as a missing avatar; the shape reads as a badge
                     * not yet earned, which is what an unearned badge is.
                     */
                    : <div className="achievements-badge_placeholder" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
                            <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
                        </svg>
                    </div>
                }
                {/* What it pays, on the art — a fourth row would break the tile's rhythm. */}
                {achievement.bonusPoints && achievement.bonusPoints > 0 &&
                    <span className="achievements-badge_bonus">+{achievement.bonusPoints}</span>
                }
                {!unlocked &&
                    <div className="achievements-badge_lock">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" />
                        </svg>
                    </div>
                }
            </div>

            <div className="achievements-badge_info">
                <span className="achievements-badge_name">{achievement.name}</span>

                {/*
                 * Art, name, tracker — the same three lines on every tile.
                 * The tracker shows on earned badges too (full bar), because a
                 * card that drops a row is a card of a different height.
                 */}
                {progress.length > 0 &&
                    <div className="achievements-badge_progress">
                        <div className="achievements-badge_progress-bar">
                            <div
                                className="achievements-badge_progress-fill"
                                style={{width: `${overallProgress}%`}}
                            />
                        </div>
                        <span className="achievements-badge_progress-text">
                            {/*
                              * The badge's OWN unit first. Every checkpoint badge
                              * shares one condition type, so the type's label read
                              * "Engagements" against a likes badge and a finds
                              * badge alike; `unit` is authored per checkpoint and
                              * says what the numbers actually count.
                              */}
                            {progress.length === 1
                                ? `${progress[0].current}/${progress[0].threshold} ${progress[0].unit || CONDITION_LABELS[progress[0].type] || ""}`.trim()
                                : `${overallProgress}%`
                            }
                        </span>
                    </div>
                }
            </div>
        </div>
    );
}

export const AchievementsBlock = ({surfaceSlug, sectionTitle}: AchievementsBlockProps) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const [activeCategory, setActiveCategory] = useState("ALL");

    const {data, isLoading} = useGetDropAchievements(surfaceSlug, fullToken);

    const filtered = useMemo(() => {
        if (!data?.achievements) return [];
        /*
         * Placement awards carry no measurable condition, so a greyed tile
         * with an empty bar is unactionable — hide them until won. Badges with
         * real checkpoints keep showing progress.
         */
        const earnable = data.achievements.filter(a => a.unlocked || a.progress.length > 0);
        if (activeCategory === "ALL") return earnable;
        return earnable.filter(a => a.achievement.category === activeCategory);
    }, [data, activeCategory]);

    /* What the rail shows, across every category — the counter's denominator. */
    const earnable = useMemo(
        () => (data?.achievements ?? []).filter(a => a.unlocked || a.progress.length > 0),
        [data],
    );
    const earnableTotal = earnable.length;

    /*
     * The collection. Permanent and independent of the ladder above — it is
     * what the member was GRANTED, so nothing recomputed can empty it.
     */
    const inventory = useMemo(() => data?.inventory ?? [], [data]);

    /*
     * Tabs come from what is actually on the surface. A campaign that authors
     * one category gets no filter at all — four chips that each empty the rail
     * are a control that only misleads.
     */
    const categoryTabs = useMemo(() => {
        const present = [...new Set(earnable.map(a => a.achievement.category))]
            .filter(c => CATEGORY_LABELS[c]);
        if (present.length < 2) return [];
        return [
            {value: "ALL", label: "All"},
            ...present.map(c => ({value: c, label: CATEGORY_LABELS[c]})),
        ];
    }, [earnable]);

    /*
     * The unlock celebration. A checkpoint clears server-side whether or not
     * the member is looking, so this fires on FIRST SIGHT of a newly-unlocked
     * badge as well as on a live refetch. Keyed by member AND surface: a
     * surface-only key let one account's unlock mark a badge seen for the next
     * account on a shared browser. It is a celebration marker, never an
     * entitlement, which is why localStorage is safe here.
     */
    const seenKey = surfaceSlug && fullToken?.id
        ? `dp.badges.seen.${surfaceSlug}.${fullToken.id}`
        : undefined;
    const [queue, setQueue] = useState<UnlockedBadge[]>([]);

    /*
     * `null` means never written, which is NOT the same as written-empty. A
     * member holding no badges banks `[]` on their first visit; treating that
     * as "first run" again would re-bank — and so swallow — the first badge
     * they ever earn.
     */
    const readSeen = (key: string): string[] | null => {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return null;
        try {
            return JSON.parse(raw) as string[];
        } catch {
            return [];
        }
    };

    /* A queue belongs to the member it was built for — see dismissBadge. */
    useEffect(() => setQueue([]), [seenKey]);

    useEffect(() => {
        if (!seenKey || !data?.achievements) return;
        const seen = readSeen(seenKey);

        /*
         * First run for a member who already holds badges is not twelve
         * modals — bank what they have and celebrate only what comes next.
         */
        if (seen === null) {
            window.localStorage.setItem(
                seenKey,
                JSON.stringify(data.achievements.filter(a => a.unlocked).map(a => a.achievement.id)),
            );
            return;
        }

        const fresh = data.achievements.filter(a => a.unlocked && !seen.includes(a.achievement.id));
        if (!fresh.length) return;

        /*
         * MERGE, and mark seen on DISMISS rather than here. A live refetch can
         * land while earlier badges are still queued; replacing the queue threw
         * those away, and marking them seen up front meant they never returned.
         */
        setQueue(prev => {
            const have = new Set(prev.map(b => b.id));
            return [
                ...prev,
                ...fresh
                    .filter(a => !have.has(a.achievement.id))
                    .map(a => ({
                        id: a.achievement.id,
                        name: a.achievement.name,
                        description: a.achievement.description,
                        iconUrl: a.achievement.iconUrl,
                    })),
            ];
        });
    }, [data, seenKey]);

    /** Dismiss is what marks a badge celebrated — see the merge note above. */
    const dismissBadge = (): void => {
        setQueue(prev => {
            const [shown, ...rest] = prev;
            if (shown && seenKey) {
                const seen = readSeen(seenKey) ?? [];
                if (!seen.includes(shown.id)) {
                    window.localStorage.setItem(seenKey, JSON.stringify([...seen, shown.id]));
                }
            }
            return rest;
        });
    };

    /*
     * Sort: families first, badges second. A family is one objective's
     * checkpoint ladder (checkpoint ids share an `checkpoint:<activation>:<objective>`
     * prefix); a standalone achievement is a family of one. Families with an
     * unlocked badge lead, then authored order — but a ladder is never split,
     * so unlocking "Earn 10 Hearts" can't strand "Earn 25 Hearts" at the end.
     */
    const sorted = useMemo(() => {
        const familyOf = (item: MyDropAchievementEntry): string =>
            item.achievement.id.startsWith("checkpoint:")
                ? item.achievement.id.split(":", 3).join(":")
                : item.achievement.id;

        const families = new Map<string, { unlocked: boolean; first: number }>();
        for (const item of filtered) {
            const fam = families.get(familyOf(item));
            if (fam) {
                fam.unlocked = fam.unlocked || item.unlocked;
                fam.first = Math.min(fam.first, item.achievement.sortOrder);
            } else {
                families.set(familyOf(item), {unlocked: item.unlocked, first: item.achievement.sortOrder});
            }
        }

        return [...filtered].sort((a, b) => {
            const fa = families.get(familyOf(a));
            const fb = families.get(familyOf(b));
            if (fa && fb && fa !== fb) {
                if (fa.unlocked !== fb.unlocked) return fa.unlocked ? -1 : 1;
                if (fa.first !== fb.first) return fa.first - fb.first;
            }
            return a.achievement.sortOrder - b.achievement.sortOrder;
        });
    }, [filtered]);

    if (isLoading) {
        return (
            <div className="achievements">
                <div className="achievements_loading">Loading achievements...</div>
            </div>
        );
    }

    /*
     * Owning something is reason enough to render. Gating on `totalActive`
     * alone hid the collection from a member whose only badges are placements —
     * they have no ladder rung, so the ladder count is zero while the member
     * holds a trophy.
     *
     * The predicate is `earnableTotal`, NOT `totalActive`, because that is what
     * every block below actually gates on. `totalActive` counts achievements
     * this rail never shows — a conditionless placement award has no progress
     * and is hidden until won — so the two diverge, and a guard that lets the
     * component through while every child suppresses itself renders an empty
     * container. Same mismatch as the bug above, one predicate over.
     */
    if (!data || earnableTotal === 0 && inventory.length === 0) {
        return null; // Nothing to work toward and nothing owned
    }

    return (
        <div className="achievements">
            {sectionTitle &&
                <h3 className="achievements_section-title">{sectionTitle}</h3>
            }

            {queue.length > 0 &&
                <BadgeUnlockModal
                    badge={queue[0]}
                    remaining={queue.length - 1}
                    onClose={dismissBadge}
                />
            }

            {/*
              * THE LADDER ONLY APPEARS IF THERE IS A LADDER.
              *
              * The block now renders when a member owns badges even with no
              * active achievements, so everything below has to ask whether the
              * ladder itself exists. Without this a stash-only member — someone
              * whose badges are all placements, which have no rung to climb —
              * got "0/0 Unlocked", an empty scroll region and "no achievements
              * to work toward" stacked above their trophies.
              */}
            {earnableTotal > 0 &&
                <div className="achievements_header">
                    <div className="achievements_counter">
                        {/* Denominator is the shown set — hidden trophies are not a goal to count against. */}
                        {data.totalUnlocked}/{earnableTotal} Unlocked
                    </div>
                </div>
            }

            {/* Category filter tabs — only when there is more than one category */}
            {categoryTabs.length > 0 &&
            <div className="achievements_tabs" role="tablist" aria-label="Badge categories">
                {categoryTabs.map(tab =>
                    <button
                        key={tab.value}
                        type="button"
                        role="tab"
                        aria-selected={activeCategory === tab.value}
                        className={`achievements_tab ${activeCategory === tab.value ? "achievements_tab--active" : ""}`}
                        onClick={() => setActiveCategory(tab.value)}
                    >
                        {tab.label}
                    </button>)}
            </div>
            }

            {/*
              * A rail, not a grid — a wall of locked tiles is not a section.
              * Focusable, because a keyboard cannot reach an overflowing scroll
              * container whose children are not themselves focusable.
              */}
            {earnableTotal > 0 &&
            <div
                className="achievements_rail"
                role="region"
                aria-label="Badges"
                /* A scrollable region must be focusable or a keyboard cannot scroll it. */
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
            >
                {sorted.map(item =>
                    <AchievementBadge key={item.achievement.id} item={item} />)}
            </div>
            }

            {earnableTotal > 0 && sorted.length === 0 &&
                <div className="achievements_empty">
                    {activeCategory === "ALL"
                        ? "No achievements to work toward yet"
                        : `No ${activeCategory.toLowerCase()} achievements yet`}
                </div>
            }

            {/*
              * ── THE COLLECTION, BENEATH THE LADDER ──
              *
              * The rail above answers "what is still out there" and is allowed
              * to move: it reads live progress, so a reset or a re-pointed
              * objective changes what it shows. This one answers "what do I
              * own", reads the GRANT, and never moves. A member who earned a
              * badge sees it here whatever happens to the counters.
              *
              * Placement badges appear ONLY here, which is what winning a place
              * means — there is no ladder rung for finishing fourth, so a greyed
              * tile could never have been the right way to show one.
              */}
            {inventory.length > 0 &&
                <>
                    <div className="achievements_header achievements_header--inventory">
                        <div className="achievements_title">My Stash</div>
                        <div className="achievements_counter achievements_counter--owned">
                            {data.totalOwned}
                        </div>
                    </div>
                    <div
                        className="achievements_rail achievements_rail--inventory"
                        role="region"
                        aria-label="My Stash — badges you own"
                        /* A scrollable region must be focusable or a keyboard cannot scroll it. */
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                        tabIndex={0}
                    >
                        {inventory.map(badge =>
                            <div className="achievements_owned" key={badge.trackerId}>
                                {badge.imageUrl
                                    ? <img
                                        className="achievements_owned_art"
                                        src={badge.imageUrl}
                                        alt=""
                                    />
                                    : <div className="achievements_owned_art achievements_owned_art--blank" />
                                }
                                <span className="achievements_owned_name">{badge.name}</span>
                                {badge.count > 1 &&
                                    <span className="achievements_owned_count">×{badge.count}</span>
                                }
                            </div>)}
                    </div>
                </>
            }
        </div>
    );
};
